// The only module that talks to the Telegram SDK, so the SDK can be swapped in one place.
import {
	backButton,
	emitEvent,
	hapticFeedback,
	init,
	isTMA,
	locationManager,
	miniApp,
	openLink,
	openTelegramLink,
	requestWriteAccess,
	mockTelegramEnv,
	retrieveLaunchParams,
	retrieveRawInitData,
	shareURL,
	themeParams,
	viewport,
} from '@tma.js/sdk-react';
import type { LatLng } from '@spot/shared';
import { useEffect } from 'react';

export interface TelegramLaunch {
	/** Raw signed initData for `POST /v1/auth/telegram`; null outside Telegram. */
	initData: string | null;
	startParam: string | null;
	languageCode: string | undefined;
	/** Telegram client platform and Bot API version (analytics only). */
	platform: string | null;
	version: string | null;
}

type HexColor = `#${string}`;

/** Reads a design token (CSS variable) so Telegram chrome matches `bg` without hard-coding hex. */
function tokenColor(name: string): HexColor | null {
	const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
	return /^#[0-9a-f]{6}$/i.test(value) ? (value as HexColor) : null;
}

function languageFrom(initData: string | null): string | undefined {
	if (initData === null) return undefined;
	try {
		const user: unknown = JSON.parse(new URLSearchParams(initData).get('user') ?? 'null');
		if (typeof user === 'object' && user !== null && 'language_code' in user) {
			return typeof user.language_code === 'string' ? user.language_code : undefined;
		}
	} catch {
		// malformed user field: fall back to the default locale
	}
	return undefined;
}

/**
 * DEV ONLY: outside Telegram, pretend to be the Telegram client with real signed initData
 * (generated by `pnpm --filter @spot/api dev:init-data`), so auth follows the production path.
 */
function mockOutsideTelegram(devInitData: string): void {
	const bg = tokenColor('--color-bg') ?? '#000000';
	const text = tokenColor('--color-text') ?? '#ffffff';
	mockTelegramEnv({
		launchParams: {
			tgWebAppData: devInitData,
			tgWebAppVersion: '8.0',
			tgWebAppPlatform: 'tdesktop',
			tgWebAppThemeParams: { bg_color: bg, text_color: text, secondary_bg_color: bg },
		},
		// Answer the requests a real client would answer, so `viewport.mount()` resolves.
		onEvent: ({ name: method }) => {
			const noInsets = { top: 0, bottom: 0, left: 0, right: 0 };
			if (method === 'web_app_request_viewport') {
				emitEvent('viewport_changed', {
					height: window.innerHeight,
					width: window.innerWidth,
					is_expanded: true,
					is_state_stable: true,
				});
			} else if (method === 'web_app_request_safe_area') {
				emitEvent('safe_area_changed', noInsets);
			} else if (method === 'web_app_request_content_safe_area') {
				emitEvent('content_safe_area_changed', noInsets);
			} else if (method === 'web_app_request_fullscreen' || method === 'web_app_exit_fullscreen') {
				emitEvent('fullscreen_changed', { is_fullscreen: false });
			}
		},
	});
}

/** Clients that never answer a viewport request must not block startup on the splash. */
const VIEWPORT_MOUNT_TIMEOUT_MS = 2000;

/** Initializes the SDK and Telegram chrome. Safe to call outside Telegram (returns nulls). */
export async function initTelegram(options: { devInitData?: string }): Promise<TelegramLaunch> {
	// Sync `isTMA()` is also true when launch params are cached in sessionStorage (after the first
	// load, even in a plain browser), so on reload the mock would be skipped and `init()` would
	// throw UnknownEnvError. `isTMA('complete')` probes the real transport instead.
	if (options.devInitData !== undefined && !(await isTMA('complete'))) {
		mockOutsideTelegram(options.devInitData);
	}
	if (!isTMA()) {
		return {
			initData: null,
			startParam: null,
			languageCode: undefined,
			platform: null,
			version: null,
		};
	}

	init();
	if (themeParams.mount.isAvailable()) themeParams.mount();
	if (miniApp.mount.isAvailable()) {
		miniApp.mount();
		const bg = tokenColor('--color-bg');
		if (bg !== null) {
			if (miniApp.setHeaderColor.isAvailable()) miniApp.setHeaderColor(bg);
			if (miniApp.setBgColor.isAvailable()) miniApp.setBgColor(bg);
			if (miniApp.setBottomBarColor.isAvailable()) miniApp.setBottomBarColor(bg);
		}
	}
	if (viewport.mount.isAvailable()) {
		try {
			await viewport.mount({ timeout: VIEWPORT_MOUNT_TIMEOUT_MS });
			// Exposes --tg-viewport-safe-area-inset-* used by the safe-area spacing tokens.
			if (viewport.bindCssVars.isAvailable()) viewport.bindCssVars();
		} catch {
			// Timed out or unsupported: the browser's env(safe-area-inset-*) fallback applies.
		}
	}
	if (viewport.expand.isAvailable()) viewport.expand();
	if (backButton.mount.isAvailable()) backButton.mount();

	const initData = retrieveRawInitData() ?? null;
	const params = retrieveLaunchParams();
	return {
		initData,
		startParam: params.tgWebAppStartParam ?? null,
		languageCode: languageFrom(initData),
		platform: params.tgWebAppPlatform,
		version: params.tgWebAppVersion,
	};
}

/** Tells Telegram the app is ready (hides its loading placeholder). */
export function markTelegramReady(): void {
	if (miniApp.ready.isAvailable()) miniApp.ready();
}

/** Shows Telegram's native BackButton while `visible`, calling `onBack` on tap. */
export function useTelegramBackButton(visible: boolean, onBack: () => void): void {
	useEffect(() => {
		if (!visible || !backButton.show.isAvailable()) return;
		backButton.show();
		const off = backButton.onClick(onBack);
		return () => {
			off();
			if (backButton.hide.isAvailable()) backButton.hide();
		};
	}, [visible, onBack]);
}

const LOCATION_TIMEOUT_MS = 10_000;

/**
 * Telegram LocationManager (DESIGN: preferred over browser geolocation). Resolves `unsupported`
 * when the client or device can't do it (caller falls back to the browser), `null` when the
 * user refused. Asks for permission only when called, i.e. on the locate button tap.
 */
export async function requestTelegramLocation(): Promise<LatLng | null | 'unsupported'> {
	try {
		if (!locationManager.isMounted()) {
			if (!locationManager.mount.isAvailable()) return 'unsupported';
			await locationManager.mount({ timeout: VIEWPORT_MOUNT_TIMEOUT_MS });
		}
		if (!locationManager.isAvailable() || !locationManager.requestLocation.isAvailable()) {
			return 'unsupported';
		}
		const result = await locationManager.requestLocation({ timeout: LOCATION_TIMEOUT_MS });
		return result === null ? null : { lat: result.latitude, lng: result.longitude };
	} catch {
		// Old client, no answer from the mock, or a request error: let the browser try.
		return 'unsupported';
	}
}

/** Telegram client platform («ios», «android», «tdesktop»…); null outside Telegram. */
export function telegramPlatform(): string | null {
	try {
		return retrieveLaunchParams().tgWebAppPlatform;
	} catch {
		return null;
	}
}

/** Opens a web page in Telegram's in-app browser (the Mini App stays open); a new tab elsewhere. */
export function openExternalLink(url: string): void {
	if (openLink.isAvailable()) {
		try {
			openLink(url);
			return;
		} catch {
			// Fall through to the browser.
		}
	}
	window.open(url, '_blank', 'noopener');
}

/** Opens a t.me link inside Telegram (a chat, a channel); a new tab elsewhere. */
export function openTelegramChat(username: string): void {
	const url = `https://t.me/${username}`;
	if (openTelegramLink.isAvailable()) {
		try {
			openTelegramLink(url);
			return;
		} catch {
			// Fall through to the browser.
		}
	}
	window.open(url, '_blank', 'noopener');
}

/** Telegram's native share sheet; false when unavailable (outside Telegram, old client). */
export function shareViaTelegram(url: string, text: string): boolean {
	if (!shareURL.isAvailable()) return false;
	try {
		shareURL(url, text);
		return true;
	} catch {
		return false;
	}
}

/**
 * D24: asks Telegram to let the bot message the user. Resolves true only on an explicit
 * "allow"; false when declined, unsupported or outside Telegram.
 */
export async function requestBotWriteAccess(): Promise<boolean> {
	if (!requestWriteAccess.isAvailable()) return false;
	try {
		return (await requestWriteAccess({ timeout: 60_000 })) === 'allowed';
	} catch {
		return false;
	}
}

/** Haptics (DESIGN → Motion): `light` on every press, outcomes use the matching kind. */
export type Haptic = 'light' | 'medium' | 'heavy' | 'selection' | 'success' | 'warning' | 'error';

/** Telegram's haptic feedback; does nothing outside Telegram or on old clients. */
export function haptic(kind: Haptic): void {
	try {
		if (kind === 'selection') {
			if (hapticFeedback.selectionChanged.isAvailable()) hapticFeedback.selectionChanged();
		} else if (kind === 'success' || kind === 'warning' || kind === 'error') {
			if (hapticFeedback.notificationOccurred.isAvailable()) {
				hapticFeedback.notificationOccurred(kind);
			}
		} else if (hapticFeedback.impactOccurred.isAvailable()) {
			hapticFeedback.impactOccurred(kind);
		}
	} catch {
		// Haptics are a nicety: never let them break a tap.
	}
}
